import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Dialog, Empty, Input, NavBar, Popup, SearchBar, Selector, Space, Switch, TextArea, Toast } from 'antd-mobile';
import { AddOutline, CloseCircleOutline } from 'antd-mobile-icons';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import API_ROUTES from '../api';
import Icon from '../icon';
import { DINNER_HAPPY_PAY_OPTIONS, isDinnerHostDisabled } from '../Dinner/dinnerStatus';
import './index.css';

const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseDateValue = (value) => {
    if (!value) return new Date();
    // H5 日期滚轮需要 Date 对象，这里统一把 yyyy-MM-dd 转成 Date。
    const [year, month, day] = String(value).split('-').map(Number);
    if (!year || !month || !day) return new Date();
    return new Date(year, month - 1, day);
};

const memberTypeMeta = (tempMember) => {
    if (tempMember === null || tempMember === undefined || tempMember === '') return { label: '常驻', order: 0 };
    if (Number(tempMember) === 3) return { label: '非正式', order: 1 };
    if (Number(tempMember) === 4) return { label: '离队', order: 2 };
    if (Number(tempMember) === 1) return { label: '临时', order: 3 };
    return { label: '其他', order: 4 };
};

export default function HappyEventCreate() {
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef(null);
    const happyId = location.state?.happyId;
    const memberInfo = useMemo(() => JSON.parse(localStorage.getItem('memberInfo') || '{}'), []);
    const [tagOptions, setTagOptions] = useState([]);
    const [memberOptions, setMemberOptions] = useState([]);
    const [gameOptions, setGameOptions] = useState([{ label: '不关联比赛', value: '' }]);
    const [uploadedMedia, setUploadedMedia] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pickerType, setPickerType] = useState('');
    const [pickerSearch, setPickerSearch] = useState('');
    const [datePickerType, setDatePickerType] = useState('');
    const [dateDraft, setDateDraft] = useState('');
    const [memberDraft, setMemberDraft] = useState([]);
    const [singleDraft, setSingleDraft] = useState('');
    const today = useMemo(() => formatDate(new Date()), []);
    const [form, setForm] = useState({
        title: '',
        eventType: '',
        content: '',
        eventDate: '',
        memberIds: [],
        gameId: '',
        dinnerId: '',
        createDinner: false,
        dinnerPosition: '',
        dinnerRemark: '',
        dinnerDate: '',
        dinnerStatus: 0,
        dinnerHostMemberIds: [],
    });

    const getMediaUrl = (item) => item?.mediaUrl || item?.media_url || item?.url || '';
    const getMediaType = (item) => Number(item?.mediaType ?? item?.media_type);

    const buildThumbUrl = (item) => {
        const url = getMediaUrl(item);
        if (!url) return '';
        if (getMediaType(item) === 2) {
            return `${url}${url.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_10000,m_fast`;
        }
        return url + Icon.ImageFooter;
    };

    const getOptionLabel = (options, value, emptyText) => {
        return options.find(item => String(item.value) === String(value))?.label || emptyText;
    };

    const getSelectedGame = (gameId = form.gameId) => {
        return gameOptions.find(item => String(item.value) === String(gameId));
    };

    const getEventDateLabel = () => {
        if (form.eventDate) return form.eventDate;
        const matchDate = getSelectedGame()?.matchDate;
        if (matchDate) return `未选择，默认比赛日期 ${matchDate}`;
        return `未选择，默认今天 ${today}`;
    };

    const getDinnerDateLabel = () => {
        if (form.dinnerDate) return form.dinnerDate;
        return '请选择聚餐时间';
    };

    const getMemberLabel = () => {
        if (!form.memberIds.length) return '不关联队员';
        return form.memberIds.map(id => memberOptions.find(item => String(item.value) === String(id))?.label).filter(Boolean).join('、');
    };

    const getHostMemberLabel = () => {
        if (!form.dinnerHostMemberIds.length) return '请选择请客人员';
        return form.dinnerHostMemberIds.map(id => memberOptions.find(item => String(item.value) === String(id))?.label).filter(Boolean).join('、');
    };

    const loadHappyTags = () => {
        return axios.get(API_ROUTES.happy_tag_list).then((response) => {
            const records = response.data?.data || [];
            const nextTags = Array.isArray(records) ? records : [];
            setTagOptions(nextTags);
            setForm(prev => ({ ...prev, eventType: prev.eventType || nextTags[0]?.id || '' }));
        }).catch(() => Toast.show({ icon: 'fail', content: '喜事标签加载失败' }));
    };

    const loadOptions = () => {
        const yearNum = new Date().getFullYear();
        axios.get(API_ROUTES.soccer_list, {
            params: { year: yearNum, type: 1 }
        }).then((response) => {
            const data = response.data?.data || [];
            // 复用相册上传页的队员接口，前端拍平成搜索选择数据。
            const records = Array.isArray(data) && data[0]?.items ? data.flatMap(group => group.items || []) : data;
            const options = (Array.isArray(records) ? records : []).map(item => {
                const typeMeta = memberTypeMeta(item.tempMember);
                return {
                    label: item.name || item.memberName || '队员',
                    value: item.id,
                    typeLabel: typeMeta.label,
                    typeOrder: typeMeta.order,
                    tempMember: item.tempMember,
                    searchText: `${item.name || ''} ${item.memberName || ''} ${item.firstNameEn || ''} ${typeMeta.label}`
                };
            }).sort((a, b) => a.typeOrder - b.typeOrder || String(a.label).localeCompare(String(b.label), 'zh-Hans-CN'));
            setMemberOptions(options);
        }).catch(() => Toast.show({ icon: 'fail', content: '队员列表加载失败' }));

        axios.get(API_ROUTES.album_dropdown_games).then((response) => {
            const records = response.data?.data || [];
            setGameOptions([{ label: '不关联比赛', value: '', searchText: '不关联比赛' }, ...(Array.isArray(records) ? records : []).map(item => ({
                label: `${item.gameName || '未命名比赛'}${item.matchTime ? ` · ${String(item.matchTime).slice(0, 10)}` : ''}`,
                value: item.id,
                matchDate: item.matchTime ? String(item.matchTime).slice(0, 10) : '',
                searchText: `${item.gameName || ''} ${item.matchTime || ''} ${item.matchAddress || ''}`
            }))]);
        }).catch(() => Toast.show({ icon: 'fail', content: '比赛列表加载失败' }));

    };

    useEffect(() => {
        loadHappyTags();
        loadOptions();
    }, []);

    useEffect(() => {
        if (!happyId) return;
        axios.get(API_ROUTES.happy_detail, { params: { id: happyId } }).then((response) => {
            const detail = response.data?.data;
            if (response.data?.code !== 200 || !detail) {
                Toast.show({ icon: 'fail', content: response.data?.msg || '喜事详情加载失败' });
                return;
            }
            setForm(prev => ({
                ...prev,
                id: detail.id,
                title: detail.title || '',
                eventType: detail.eventType || prev.eventType,
                content: detail.content || '',
                eventDate: detail.eventDate || '',
                memberIds: detail.memberIds || [],
                gameId: detail.gameId || '',
                dinnerId: detail.dinnerId || '',
                createDinner: false,
                dinnerPosition: '',
                dinnerRemark: '',
                dinnerDate: '',
                dinnerStatus: 0,
                dinnerHostMemberIds: [],
            }));
            setUploadedMedia((detail.mediaList || []).map((media, index) => ({
                id: media.id || `${detail.id}-${index}`,
                mediaUrl: getMediaUrl(media),
                mediaType: getMediaType(media) || 1,
                fileName: media.fileName || media.file_name || ''
            })).filter(item => item.mediaUrl));
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '喜事详情加载失败' });
        });
    }, [happyId]);

    const addHappyTag = () => {
        let tagName = '';
        Dialog.confirm({
            title: '新增喜事标签',
            content: <Input placeholder="例如：升职、乔迁、毕业" onChange={(value) => { tagName = value; }} />,
            onConfirm: () => {
                if (!tagName.trim()) {
                    Toast.show({ content: '请输入标签名称' });
                    return Promise.reject();
                }
                return axios.post(API_ROUTES.happy_tag_add, null, {
                    params: { tagName: tagName.trim() },
                    headers: { Authorization: memberInfo?.id || '' }
                }).then((response) => {
                    if (response.data?.code === 200) {
                        Toast.show({ icon: 'success', content: '标签已新增' });
                        return loadHappyTags();
                    }
                    Toast.show({ icon: 'fail', content: response.data?.msg || '新增失败' });
                    return Promise.reject();
                });
            }
        });
    };

    const deleteHappyTag = (tagId) => {
        Dialog.confirm({
            title: '删除喜事标签',
            content: '确认删除这个喜事标签吗？',
            onConfirm: () => axios.post(API_ROUTES.happy_tag_delete, null, {
                params: { tagId },
                headers: { Authorization: memberInfo?.id || '' }
            }).then((response) => {
                if (response.data?.code === 200) {
                    Toast.show({ icon: 'success', content: '标签已删除' });
                    if (String(form.eventType) === String(tagId)) {
                        setForm(prev => ({ ...prev, eventType: tagOptions.find(tag => String(tag.id) !== String(tagId))?.id || '' }));
                    }
                    return loadHappyTags();
                }
                Toast.show({ icon: 'fail', content: response.data?.msg || '删除失败' });
                return Promise.reject();
            })
        });
    };

    const handleChooseFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;
        setUploading(true);
        try {
            // 喜事媒体先上传到 OSS，保存喜事时由后端同步写入喜事媒体表和球队相册。
            const records = await Promise.all(files.map(async (file, index) => {
                const formData = new FormData();
                formData.append('file', file);
                const response = await axios.post(API_ROUTES.auth_uploadToOss, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                if (response.data?.code !== 200) {
                    throw new Error(response.data?.msg || `${file.name} 上传失败`);
                }
                return {
                    id: `${Date.now()}-${index}-${file.name}`,
                    mediaUrl: response.data?.data,
                    mediaType: file.type?.startsWith('video/') ? 2 : 1,
                    fileName: file.name
                };
            }));
            setUploadedMedia(prev => [...prev, ...records]);
            Toast.show({ icon: 'success', content: '上传完成' });
        } catch (error) {
            Toast.show({ icon: 'fail', content: error.message || '上传失败' });
        } finally {
            setUploading(false);
        }
    };

    const removeUploadedMedia = (mediaId) => {
        setUploadedMedia(prev => prev.filter(item => String(item.id) !== String(mediaId)));
    };

    const submitHappy = async () => {
        if (!form.title.trim()) return Toast.show({ content: '请输入喜事标题' });
        if (!form.eventType) return Toast.show({ content: '请选择喜事标签' });
        if (form.createDinner && !form.dinnerPosition.trim()) return Toast.show({ content: '请输入聚餐地点' });
        if (form.createDinner && !form.dinnerDate) return Toast.show({ content: '请选择聚餐时间' });
        if (form.createDinner && !isDinnerHostDisabled(form.dinnerStatus) && !form.dinnerHostMemberIds.length) return Toast.show({ content: '请选择请客人员' });
        setSaving(true);
        try {
            let dinnerId = '';
            if (form.createDinner) {
                // 喜事请客会先创建聚餐，再把返回的 dinnerId 绑定到喜事记录。
                const dinnerResponse = await axios.post(API_ROUTES.dinner_create, {
                    gameId: form.gameId || '',
                    position: form.dinnerPosition.trim(),
                    remark: form.dinnerRemark.trim() || `${form.title.trim()}请客聚餐`,
                    lat: 0,
                    lng: 0,
                    status: form.dinnerStatus,
                    dinnerDate: form.dinnerDate,
                    hostMemberIds: !isDinnerHostDisabled(form.dinnerStatus) ? form.dinnerHostMemberIds : []
                }, {
                    headers: { Authorization: memberInfo?.id || '' }
                });
                if (dinnerResponse.data?.code !== 200) {
                    throw new Error(dinnerResponse.data?.msg || '聚餐创建失败');
                }
                dinnerId = dinnerResponse.data?.data || '';
            }
            const response = await axios.post(API_ROUTES.happy_save, {
                ...form,
                title: form.title.trim(),
                content: form.content.trim(),
                createMemberId: memberInfo?.id,
                dinnerId,
                mediaUrls: uploadedMedia.map(item => getMediaUrl(item)).filter(Boolean),
                mediaTypes: uploadedMedia.map(item => getMediaType(item) || 1),
                mediaNames: uploadedMedia.map(item => item.fileName || item.file_name || '')
            });
            if (response.data?.code === 200) {
                Toast.show({ icon: 'success', content: '喜事已记录' });
                navigate('/happyEvent');
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '保存失败' });
            }
        } catch (error) {
            Toast.show({ icon: 'fail', content: error.message || '保存失败' });
        } finally {
            setSaving(false);
        }
    };

    const openPicker = (type) => {
        setPickerType(type);
        setPickerSearch('');
        if (type === 'member') {
            setMemberDraft(form.memberIds);
        } else if (type === 'host') {
            setMemberDraft(form.dinnerHostMemberIds);
        } else if (type === 'game') {
            setSingleDraft(form.gameId);
        }
    };

    const closePicker = () => {
        setPickerType('');
        setPickerSearch('');
        setMemberDraft([]);
        setSingleDraft('');
    };

    const currentPicker = useMemo(() => {
        if (pickerType === 'member') {
            return { title: '选择关联队员', placeholder: '搜索队员姓名或类型', options: memberOptions, multiple: true, grid: true };
        }
        if (pickerType === 'host') {
            return { title: '选择请客人员', placeholder: '搜索队员姓名或类型', options: memberOptions, multiple: true, grid: true };
        }
        if (pickerType === 'game') {
            return { title: '选择关联比赛', placeholder: '搜索比赛名称、日期或地点', options: gameOptions };
        }
        return null;
    }, [gameOptions, memberOptions, pickerType]);

    const filteredOptions = useMemo(() => {
        if (!currentPicker) return [];
        const keyword = pickerSearch.trim().toLowerCase();
        if (!keyword) return currentPicker.options;
        return currentPicker.options.filter(item => String(item.searchText || item.label || '').toLowerCase().includes(keyword));
    }, [currentPicker, pickerSearch]);

    const isOptionSelected = (value) => {
        if (pickerType === 'member' || pickerType === 'host') {
            return memberDraft.some(id => String(id) === String(value));
        }
        return String(singleDraft) === String(value);
    };

    const toggleMemberDraft = (value) => {
        setMemberDraft(prev => prev.some(id => String(id) === String(value))
            ? prev.filter(id => String(id) !== String(value))
            : [...prev, value]);
    };

    const confirmPicker = () => {
        if (pickerType === 'member') {
            setForm(prev => ({ ...prev, memberIds: memberDraft }));
        } else if (pickerType === 'host') {
            setForm(prev => ({ ...prev, dinnerHostMemberIds: memberDraft }));
        } else if (pickerType === 'game') {
            setForm(prev => {
                const matchDate = getSelectedGame(singleDraft)?.matchDate || '';
                return {
                    ...prev,
                    gameId: singleDraft,
                    eventDate: prev.eventDate || matchDate,
                    dinnerDate: prev.dinnerDate || matchDate
                };
            });
        }
        closePicker();
    };

    const openDatePicker = (type) => {
        setDatePickerType(type);
        setDateDraft(type === 'dinner' ? (form.dinnerDate || getSelectedGame()?.matchDate || form.eventDate || today) : (form.eventDate || getSelectedGame()?.matchDate || today));
    };

    const closeDatePicker = () => {
        setDatePickerType('');
        setDateDraft('');
    };

    const confirmDatePicker = (date) => {
        const nextDate = formatDate(date || new Date());
        if (datePickerType === 'event') {
            setForm(prev => ({ ...prev, eventDate: nextDate }));
        } else if (datePickerType === 'dinner') {
            setForm(prev => ({ ...prev, dinnerDate: nextDate }));
        }
        closeDatePicker();
    };

    return (
        <div className="happy-page happy-create-page has-unified-nav">
            <NavBar className="unified-navbar fixed" onBack={() => navigate('/happyEvent')}>{happyId ? '修改喜事' : '记录喜事'}</NavBar>
            <section className="happy-hero happy-create-hero">
                <div>
                    <span className="happy-kicker">{happyId ? 'EDIT MOMENT' : 'CREATE MOMENT'}</span>
                    <h1>{happyId ? '更新这份喜悦' : '把这一刻留下来'}</h1>
                    <p>可以先上传多张图片/视频，再绑定队员、日期、比赛，并同步创建请客聚餐。</p>
                </div>
                <div className="happy-hero-icon">🥂</div>
            </section>

            <section className="happy-create-card">
                <label>上传照片/视频</label>
                <input ref={fileInputRef} className="happy-hidden-file" type="file" accept="image/*,video/*" multiple onChange={handleChooseFiles} />
                <button type="button" className="happy-upload-drop" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    <AddOutline />
                    <strong>{uploading ? '上传中...' : uploadedMedia.length ? '继续增加' : '选择文件上传'}</strong>
                    <span>支持一次选择多个图片或视频</span>
                </button>
                {uploadedMedia.length > 0 && (
                    <div className="happy-upload-preview-grid">
                        {uploadedMedia.map(media => (
                            <div key={media.id} className="happy-upload-preview">
                                {getMediaType(media) === 2 && <i>▶</i>}
                                <img src={buildThumbUrl(media)} alt={media.fileName || '已上传影像'} />
                                <button type="button" onClick={() => removeUploadedMedia(media.id)}><CloseCircleOutline /></button>
                            </div>
                        ))}
                    </div>
                )}

                <label>喜事标题</label>
                <Input className="happy-input" placeholder="例如：发表文章" value={form.title} onChange={(value) => setForm(prev => ({ ...prev, title: value }))} />

                <label>喜事标签</label>
                <div className="happy-tag-manager">
                    {/* 标签与相册保持同样的管理体验，管理员可以直接新增或删除 */}
                    <div className="happy-tag-list">
                        {tagOptions.map(tag => (
                            <span key={tag.id} className={`happy-tag-chip ${String(form.eventType) === String(tag.id) ? 'active' : ''}`}>
                                <button type="button" onClick={() => setForm(prev => ({ ...prev, eventType: tag.id }))}>{tag.tagName}</button>
                                {memberInfo?.admin === 1 && (
                                    <button type="button" className="happy-tag-delete" onClick={() => deleteHappyTag(tag.id)}>×</button>
                                )}
                            </span>
                        ))}
                        {memberInfo?.admin === 1 && (
                            <button type="button" className="happy-tag-add" onClick={addHappyTag}>+ 增加标签</button>
                        )}
                    </div>
                </div>

                <label>关联队员（可选）</label>
                <button type="button" className="happy-picker-field" onClick={() => openPicker('member')}>
                    <span>{getMemberLabel()}</span>
                    <i>选择</i>
                </button>

                <label>文字描述</label>
                <TextArea className="happy-textarea" placeholder="写下这件喜事的来龙去脉" rows={4} value={form.content} onChange={(value) => setForm(prev => ({ ...prev, content: value }))} />

                <label>喜事时间</label>
                <button type="button" className="happy-picker-field" onClick={() => openDatePicker('event')}>
                    <span>{getEventDateLabel()}</span>
                    <i>选择</i>
                </button>

                <label>关联比赛</label>
                <button type="button" className="happy-picker-field" onClick={() => openPicker('game')}>
                    <span>{getOptionLabel(gameOptions, form.gameId, '不关联比赛')}</span>
                    <i>选择</i>
                </button>

                {!form.dinnerId && <label>是否创建请客聚餐</label>}
                {!form.dinnerId && <div className="happy-dinner-create-card">
                    <div className="happy-dinner-create-head">
                        <div>
                            <strong>{form.createDinner ? '创建一场聚餐' : '不创建聚餐'}</strong>
                            <span>{form.createDinner ? '保存喜事时会同步生成聚餐记录' : '只记录喜事，不生成聚餐'}</span>
                        </div>
                        <Switch
                            checked={form.createDinner}
                            onChange={(checked) => setForm(prev => ({ ...prev, createDinner: checked }))}
                        />
                    </div>
                    {form.createDinner && (
                        <div className="happy-dinner-create-fields">
                            <button type="button" className="happy-picker-field" onClick={() => openDatePicker('dinner')}>
                                <span>{getDinnerDateLabel()}</span>
                                <i>选择时间</i>
                            </button>
                            <Input className="happy-input" placeholder="聚餐地点，例如：科华路某火锅店" value={form.dinnerPosition} onChange={(value) => setForm(prev => ({ ...prev, dinnerPosition: value }))} />
                            <Input className="happy-input" placeholder="聚餐备注，可选" value={form.dinnerRemark} onChange={(value) => setForm(prev => ({ ...prev, dinnerRemark: value }))} />
                            <Selector
                                columns={3}
                                value={[form.dinnerStatus]}
                                options={DINNER_HAPPY_PAY_OPTIONS}
                                onChange={(value) => setForm(prev => ({ ...prev, dinnerStatus: value[0] ?? 0, dinnerHostMemberIds: isDinnerHostDisabled(value[0] ?? 0) ? [] : prev.dinnerHostMemberIds }))}
                            />
                            {!isDinnerHostDisabled(form.dinnerStatus) && (
                                <button type="button" className="happy-picker-field" onClick={() => openPicker('host')}>
                                    <span>{getHostMemberLabel()}</span>
                                    <i>选择</i>
                                </button>
                            )}
                        </div>
                    )}
                </div>}

                <Button block color="primary" loading={saving} className="happy-submit-btn" onClick={submitHappy}>{happyId ? '保存修改' : '保存喜事'}</Button>
                <Space block justify="center" className="happy-form-tip">创建聚餐时会自动绑定当前喜事；未选择比赛也可以创建。</Space>
            </section>

            <Popup visible={!!currentPicker} onMaskClick={closePicker} bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22, maxHeight: '82vh', overflow: 'hidden' }}>
                {currentPicker && (
                    <div className="happy-picker-popup">
                        <div className="happy-picker-fixed">
                            <div className="happy-form-head">
                                <button type="button" onClick={closePicker}>取消</button>
                                <strong>{currentPicker.title}</strong>
                                <button type="button" onClick={confirmPicker}>确认</button>
                            </div>
                            <SearchBar className="happy-picker-search" placeholder={currentPicker.placeholder} value={pickerSearch} onChange={setPickerSearch} />
                        </div>
                        <div className={`happy-picker-list ${currentPicker.grid ? 'member-grid' : ''}`}>
                            {filteredOptions.length > 0 ? filteredOptions.map(option => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`happy-picker-option ${isOptionSelected(option.value) ? 'active' : ''}`}
                                    onClick={() => currentPicker.multiple ? toggleMemberDraft(option.value) : setSingleDraft(option.value)}
                                >
                                    <span>{option.label}</span>
                                    {currentPicker.grid && <em>{option.typeLabel}</em>}
                                    <i>{isOptionSelected(option.value) ? '已选' : ''}</i>
                                </button>
                            )) : (
                                <Empty description="没有匹配的数据" imageStyle={{ width: 80 }} />
                            )}
                        </div>
                    </div>
                )}
            </Popup>

            <DatePicker
                visible={!!datePickerType}
                title={datePickerType === 'dinner' ? '选择聚餐时间' : '选择喜事时间'}
                precision="day"
                value={parseDateValue(dateDraft)}
                min={new Date(2010, 0, 1)}
                max={new Date(2035, 11, 31)}
                onClose={closeDatePicker}
                onConfirm={confirmDatePicker}
            />
        </div>
    );
}
